<template>
  <div class="no-tabs sent-geo-modal user-form">
    <form
      autocomplete="off"
      @submit.prevent="submit"
    >
      <div class="row justify-content-center">
        <div class="col-md-6">
          <h3>
            <span
              v-if="isEdit"
              class="edit"
            >{{ $t('modules.users.edit_head') }}</span>
            <template v-if="isEdit">
              {{ model.name }} {{ model.surname }}
            </template>
            <template v-else>
              {{ $t('modules.users.new_head') }}
            </template>
          </h3>

          <div class="mb-3">
            <label class="form-label">E-mail <span class="text-danger">*</span></label>
            <input
              v-model="model.email"
              :disabled="isEdit"
              type="email"
              class="form-control"
              :class="{ 'is-invalid': v.model.email.$error }"
              autocomplete="off"
              @blur="v.model.email.$touch()"
            >
            <div class="invalid-feedback">
              {{ emailError }}
            </div>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">{{ $t('modules.users.name') }} *</label>
              <input
                v-model="model.name"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': v.model.name.$error }"
                autocomplete="off"
                @blur="v.model.name.$touch()"
              >
              <div class="invalid-feedback">
                {{ nameError }}
              </div>
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">{{ $t('modules.users.surname') }} *</label>
              <input
                v-model="model.surname"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': v.model.surname.$error }"
                autocomplete="off"
                @blur="v.model.surname.$touch()"
              >
              <div class="invalid-feedback">
                {{ surnameError }}
              </div>
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label">{{ $t('modules.users.phone') }}</label>
            <input
              v-model="model.phone"
              type="text"
              class="form-control"
              autocomplete="off"
            >
          </div>

          <div class="mb-3">
            <label class="form-label">{{ $t('modules.users.driver_card') }}</label>
            <input
              v-model="model.driver_card"
              type="text"
              class="form-control"
              autocomplete="off"
            >
          </div>

          <div class="mb-3">
            <label class="form-label">{{ $t('modules.users.driver_card_validity') }}</label>
            <input
              v-model="model.driver_card_validity"
              type="date"
              class="form-control"
            >
          </div>

          <div class="form-check mb-3">
            <input
              v-model="model.active"
              class="form-check-input"
              type="checkbox"
              id="activeCheck"
            >
            <label
              class="form-check-label"
              for="activeCheck"
            >
              {{ $t('modules.users.active') }}
            </label>
          </div>

          <div class="mb-3">
            <label class="form-label">{{ $t('modules.users.roles') }} *</label>
            <VueSelect
              :key="model.roles.join('|')"
              :options="availableRoles"
              :multiple="true"
              label="label"
              icon="bi bi-shield-lock"
              class="vue-select flex-grow"
              @updateSelect="onRolesSelect"
              @blur="v.model.roles.$touch()"
              :value="selectedRoleOptions"
            />
            <div class="invalid-feedback d-block">
              {{ rolesError }}
            </div>
            <small class="text-muted">{{ $t('modules.users.roles_hint') }}</small>
          </div>

          <div class="mb-3">
            <label class="form-label">
              {{ $t('modules.users.password') }}
              <span
                v-if="!isEdit"
                class="text-danger"
              >*</span>
            </label>
            <input
              v-model="newPassword"
              type="password"
              name="new-password"
              autocomplete="new-password"
              class="form-control"
              :class="{ 'is-invalid': passwordError }"
              @blur="v.newPassword.$touch()"
              :placeholder="isEdit ? $t('modules.users.password_hint') : ''"
            >
            <div
              class="invalid-feedback"
              v-if="passwordError"
            >
              {{ passwordError }}
            </div>
          </div>

          <button
            type="submit"
            class="btn btn-primary"
          >
            {{ $t('modules.users.save') }}
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue'
import VueSelect from '@/components/extras/VueSelect.vue'
import {User} from './types'
import useVuelidate from '@vuelidate/core'
import {email, required, minLength} from '@/vuelidate/i18n-validators'
import {createUser, getUser, updateUser, getStore} from './crud'
import {userDetails} from '@/utils/tools'

export default defineComponent({
  name: 'UserForm',
  components: {VueSelect},
  setup() {
    return {v: useVuelidate()}
  },
  data() {
    return {
      isEdit: false as boolean,
      originalEmail: '' as string,
      newPassword: '' as string,
      model: {
        email: null,
        name: null,
        surname: null,
        phone: null,
        roles: ['ROLE_DRIVER'],
        driver_card: null,
        driver_card_validity: null,
        active: true,
        company: null
      } as User,
      baseRolesOptions: [
        {label: 'DRIVER', value: 'ROLE_DRIVER'},
        {label: 'USER', value: 'ROLE_USER'},
        {label: 'BOSS', value: 'ROLE_BOSS'},
      ] as { label: string; value: string }[],
      store: getStore(),
    }
  },
  validations() {
    return {
      model: {
        email: {required, email},
        name: {required},
        surname: {required},
        roles: {required}
      },
      newPassword: this.isEdit
          ? {minLength: minLength(6)}
          : {required, minLength: minLength(6)}
    }
  },
  computed: {
    availableRoles(): { label: string; value: string }[] {
      const opts = this.baseRolesOptions.map(opt => ({
        value: opt.value,
        label: this.$t(`modules.users.user_roles.${opt.value}`) as string
      }))

      const hasAnyModuleCompany =
          Array.isArray(this.model.company?.client_modules) &&
          this.model.company.client_modules.length > 0

      const jwt = userDetails() as any
      const hasAnyModuleJwt =
          Array.isArray(jwt?.modules) &&
          jwt.modules.length > 0

      const alreadyHasRole = this.model.roles.includes('ROLE_FORMS_MOBILE')

      if ((hasAnyModuleCompany || hasAnyModuleJwt || alreadyHasRole) &&
          !opts.find(o => o.value === 'ROLE_FORMS_MOBILE')) {
        opts.push({
          value: 'ROLE_FORMS_MOBILE',
          label: this.$t('modules.users.user_roles.FORMS_MOBILE') as string
        })
      }

      return opts
    },
    selectedRoleOptions(): { label: string; value: string }[] {
      return this.availableRoles.filter(o =>
          this.model.roles.includes(o.value)
      )
    },
    emailError(): string {
      const fld = this.v.model.email
      if (!fld.$dirty) return ''
      if (fld.$error) {
        return fld.$errors[0]?.$message || ''
      }
      return ''
    },

    nameError(): string {
      const fld = this.v.model.name
      if (!fld.$dirty) return ''
      if (fld.$error) {
        return fld.$errors[0]?.$message || ''
      }
      return ''
    },

    surnameError(): string {
      const fld = this.v.model.surname
      if (!fld.$dirty) return ''
      if (fld.$error) {
        return fld.$errors[0]?.$message || ''
      }
      return ''
    },

    rolesError(): string {
      const fld = this.v.model.roles
      if (!fld.$dirty) return ''
      if (fld.$error) {
        return fld.$errors[0]?.$message || ''
      }
      return ''
    },
    passwordError(): string {
      if (this.isEdit) return ''
      const fld = this.v.newPassword
      if (!fld.$dirty) return ''
      if (fld.$error) {
        return fld.$errors[0]?.$message || ''
      }
      return ''
    }
  },
  methods: {
    cleanUserPayload(payload: any) {
      ['email', 'name', 'surname', 'driver_card'].forEach(f => {
        if (typeof payload[f] === 'string') payload[f] = payload[f].trim();
      });
      if (typeof payload.password === 'string') {
        payload.password = payload.password.replace(/\s+/g, '');
      }
      if (typeof payload.phone === 'string') {
        payload.phone = payload.phone.replace(/\s+/g, '');
      }
      return payload;
    },
    onRolesSelect(val: Array<{ label: string; value: string }> | null) {
      if (!val || val.length === 0) {
        return
      }
      const newRoles = val.map(o => o.value)
      if (
          newRoles.length === this.model.roles.length &&
          newRoles.every(r => this.model.roles.includes(r))
      ) {
        return
      }
      this.model.roles = newRoles
    },

    async loadData(email: string) {
      const resp = await getUser(email)
      const data = {...resp.data, company: resp.data.company}

      if (data.driver_card_validity) {
        data.driver_card_validity = (data.driver_card_validity as string).split('T')[0]
      }

      this.model = data as User
      this.isEdit = true
      this.newPassword = ''
    },

    resetForm() {
      this.isEdit = false
      this.originalEmail = ''
      this.newPassword = ''
      this.model = {
        email: null,
        name: null,
        surname: null,
        phone: null,
        roles: ['ROLE_DRIVER'],
        driver_card: null,
        driver_card_validity: null,
        active: true,
        company: null
      }
    },

    async submit() {
      this.v.$touch()
      if (this.v.$invalid) return

      const currentCompanyId = userDetails()?.company

      const payload: any = {
        email: this.model.email,
        name: this.model.name,
        surname: this.model.surname,
        roles: this.model.roles,
        active: this.model.active,
        driver_card: this.model.driver_card,
        driver_card_validity: this.model.driver_card_validity,
        phone: this.model.phone,
      }
      if (this.newPassword) {
        payload.password = this.newPassword
      }
      if (!this.isEdit) {
        payload.company = this.model.company ?? currentCompanyId
      }
      this.cleanUserPayload(payload);

      try {
        if (this.isEdit) {
          await updateUser(this.originalEmail, payload)
        } else {
          await createUser(payload)
        }
        this.$toast.success(this.$t('modules.users.saved') as string)
        this.$router.push('/users')
      } catch (e: any) {
        const key = e.message || 'modules.users.save_error'
        this.$toast.error(this.$t(key) as string)
      }
    },
  },
  async created() {
    const idParam = this.$route.params.id as string | undefined
    if (idParam) {
      const id = Number(idParam)
      const resp: { data: any[]; totalCount: number } = await (this.store as any).load({
        filter: ['id', '=', id],
        skip: 0,
        take: 1
      })
      const user = resp.data[0]
      this.originalEmail = user.email
      this.model = {
        ...user,
        driver_card_validity: user.driver_card_validity
            ? (user.driver_card_validity as string).split('T')[0]
            : null
      }
      this.isEdit = true
    } else {
      this.resetForm()
    }
  },
})
</script>
<style lang="scss">
.user-form {
  .edit {
    background: #f67f01;
    color: #fff;
    padding: 4px 8px;
    font-size: 11px;
    border-radius: 4px;
    margin-right: 10px;
    text-transform: uppercase;
  }

  .vue-select {
    width: 100%;
  }
}
</style>
